import {
  ExternalLink,
  Copy,
  Printer,
  FileSpreadsheet,
  CheckCircle,
  DollarSign,
  Send,
  FileText,
  Eye,
  Pencil,
  Ban,
  Receipt,
  Trash2,
} from "lucide-react";
import type { ContextAction, EntityContextType } from "./types";

const ALL_EXTERNAL: ("customer" | "vendor" | "personnel" | "contract_labor")[] =
  ["customer", "vendor", "personnel", "contract_labor"];

const ACTION_REGISTRY: Record<EntityContextType, ContextAction[]> = {
  projects: [
    {
      id: "open",
      label: "Open Project",
      icon: ExternalLink,
      handler: "navigate",
      path: (id) => `/controller/projects/${id}`,
    },
    {
      id: "open_new_tab",
      label: "Open in New Tab",
      icon: ExternalLink,
      handler: "navigate_new_tab",
      path: (id) => `/controller/projects/${id}`,
    },
    {
      id: "print",
      label: "Print Summary",
      icon: Printer,
      handler: "print",
      divider: true,
    },
    {
      id: "export_excel",
      label: "Export to Excel",
      icon: FileSpreadsheet,
      handler: "custom",
      hideForRoles: ALL_EXTERNAL,
    },
    {
      id: "copy_number",
      label: "Copy Project #",
      icon: Copy,
      handler: "copy",
      copyField: "project_number",
    },
    {
      id: "delete_project",
      label: "Delete Project",
      icon: Trash2,
      handler: "custom",
      divider: true,
      destructive: true,
      permission: "edit_project_info",
      hideForRoles: ALL_EXTERNAL,
    },
  ],
  invoices: [
    {
      id: "open",
      label: "Open Invoice",
      icon: ExternalLink,
      handler: "navigate",
      path: (id) => `/controller/invoices?id=${id}`,
    },
    { id: "print", label: "Print", icon: Printer, handler: "print" },
    {
      id: "export_pdf",
      label: "Export PDF",
      icon: FileText,
      handler: "custom",
    },
    {
      id: "copy_number",
      label: "Copy Invoice #",
      icon: Copy,
      handler: "copy",
      copyField: "invoice_number",
    },
    {
      id: "mark_sent",
      label: "Mark Sent",
      icon: Send,
      handler: "custom",
      divider: true,
      permission: "approve_invoices",
      hideForRoles: ALL_EXTERNAL,
    },
    {
      id: "record_payment",
      label: "Record Payment",
      icon: DollarSign,
      handler: "custom",
      permission: "approve_invoices",
      hideForRoles: ALL_EXTERNAL,
    },
  ],
  bills: [
    {
      id: "open",
      label: "Open Bill",
      icon: ExternalLink,
      handler: "navigate",
      path: (id) => `/controller/bills?id=${id}`,
    },
    { id: "print", label: "Print", icon: Printer, handler: "print" },
    {
      id: "export_pdf",
      label: "Export PDF",
      icon: FileText,
      handler: "custom",
    },
    {
      id: "approve",
      label: "Approve",
      icon: CheckCircle,
      handler: "custom",
      divider: true,
      permission: "approve_bills",
      hideForRoles: ALL_EXTERNAL,
    },
    {
      id: "mark_paid",
      label: "Mark as Paid",
      icon: DollarSign,
      handler: "custom",
      permission: "approve_payments",
      hideForRoles: ALL_EXTERNAL,
    },
    {
      id: "void",
      label: "Void Bill",
      icon: Ban,
      handler: "custom",
      permission: "approve_bills",
      hideForRoles: ALL_EXTERNAL,
    },
  ],
  vendors: [
    {
      id: "open",
      label: "View Vendor",
      icon: Eye,
      handler: "navigate",
      path: (id) => `/controller/vendors/${id}`,
    },
    {
      id: "open_new_tab",
      label: "Open in New Tab",
      icon: ExternalLink,
      handler: "navigate_new_tab",
      path: (id) => `/controller/vendors/${id}`,
    },
    {
      id: "copy_name",
      label: "Copy Name",
      icon: Copy,
      handler: "copy",
      copyField: "name",
    },
  ],
  customers: [
    {
      id: "open",
      label: "View Customer",
      icon: Eye,
      handler: "navigate",
      path: (id) => `/controller/customers/${id}`,
    },
    {
      id: "open_new_tab",
      label: "Open in New Tab",
      icon: ExternalLink,
      handler: "navigate_new_tab",
      path: (id) => `/controller/customers/${id}`,
    },
    {
      id: "copy_name",
      label: "Copy Name",
      icon: Copy,
      handler: "copy",
      copyField: "name",
    },
  ],
  work_orders: [
    {
      id: "open",
      label: "Open Work Order",
      icon: ExternalLink,
      handler: "custom",
    },
    { id: "print", label: "Print Summary", icon: Printer, handler: "print" },
    {
      id: "create_bill",
      label: "Create Draft Bill",
      icon: Receipt,
      handler: "custom",
      divider: true,
      permission: "approve_bills",
      hideForRoles: ALL_EXTERNAL,
    },
    {
      id: "copy_number",
      label: "Copy WO #",
      icon: Copy,
      handler: "copy",
      copyField: "number",
    },
  ],
  change_orders: [
    { id: "print", label: "Print", icon: Printer, handler: "print" },
    {
      id: "export_pdf",
      label: "Export PDF",
      icon: FileText,
      handler: "custom",
    },
    {
      id: "edit",
      label: "Edit",
      icon: Pencil,
      handler: "custom",
      hideForRoles: ALL_EXTERNAL,
    },
    {
      id: "revert",
      label: "Revert to Draft",
      icon: Ban,
      handler: "custom",
      hideForRoles: ALL_EXTERNAL,
    },
    {
      id: "approve",
      label: "Approve",
      icon: CheckCircle,
      handler: "custom",
      divider: true,
      permission: "approve_bills",
      hideForRoles: ALL_EXTERNAL,
    },
    {
      id: "copy_number",
      label: "Copy CO #",
      icon: Copy,
      handler: "copy",
      copyField: "number",
    },
  ],
  expenses: [
    {
      id: "approve",
      label: "Approve",
      icon: CheckCircle,
      handler: "custom",
      permission: "approve_bills",
      hideForRoles: ALL_EXTERNAL,
    },
    {
      id: "copy_desc",
      label: "Copy Description",
      icon: Copy,
      handler: "copy",
      copyField: "description",
    },
  ],
  approvals: [
    { id: "open", label: "View Request", icon: Eye, handler: "custom" },
    { id: "print", label: "Print Summary", icon: Printer, handler: "print" },
    {
      id: "copy_title",
      label: "Copy Title",
      icon: Copy,
      handler: "copy",
      copyField: "title",
    },
  ],
  project_documents: [
    {
      id: "open_new_tab",
      label: "Open in New Tab",
      icon: ExternalLink,
      handler: "custom",
    },
    {
      id: "download",
      label: "Download",
      icon: FileText,
      handler: "custom",
    },
    {
      id: "copy_link",
      label: "Copy Link",
      icon: Copy,
      handler: "custom",
    },
    {
      id: "delete",
      label: "Delete",
      icon: Trash2,
      handler: "custom",
      divider: true,
      destructive: true,
      permission: "edit_project_info",
      hideForRoles: ALL_EXTERNAL,
    },
  ],
};

export function getContextActions(
  entityType: EntityContextType
): ContextAction[] {
  return ACTION_REGISTRY[entityType] ?? [];
}
